/* ==========================================================================
   Virsaay — search

   The panel rests at translateY(-100%) in CSS so it can never flash before JS
   takes over. Open is therefore a real state — the `is-open` class — not just a
   GSAP position: GSAP is a deferred CDN script, so a visitor who clicks the
   search icon before it lands (or who prefers reduced motion, or is on a
   blocked network) must still get a panel on screen.
   ========================================================================== */

(function () {
  'use strict';

  var SELECTOR = '[data-search-panel]';

  // What the predictive-search section renders. Ask for all of it explicitly,
  // and per type: the default limit_scope caps the TOTAL across every type, so
  // a handful of product hits would otherwise crowd out the whole Suggestions
  // group that the section renders underneath them.
  var RESOURCES = 'product,query,collection,page,article';
  var RESOURCE_LIMIT = 4;
  var RESOURCE_SCOPE = 'each';

  function motion() {
    return window.VirsaayMotion || null;
  }

  function reduced() {
    var m = motion();
    return !window.gsap || !m || m.reduced;
  }

  function initSearch(root) {
    if (!root || root.dataset.searchReady === 'true') return;
    root.dataset.searchReady = 'true';

    var form = root.querySelector('[data-predictive-search-form]');
    var input = root.querySelector('[data-predictive-search-input]');
    var results = root.querySelector('[data-predictive-search-results]');
    var suggestions = root.querySelector('[data-search-suggestions]');

    var isOpen = false;
    var controller = null;
    var timer = null;
    var activeElement = null;

    function scrim() {
      return document.querySelector('[data-scrim]');
    }

    function setExpanded(open) {
      document.querySelectorAll('[data-search-open]').forEach(function (btn) {
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }

    function open(e) {
      if (e && e.preventDefault) e.preventDefault();
      if (isOpen) return;
      isOpen = true;
      activeElement = document.activeElement;

      // One overlay at a time: fold away the cart and the menus first, so the
      // shared scrim is only ever owned by whoever opened last.
      document.dispatchEvent(new CustomEvent('virsaay:cart:close'));
      var header = document.querySelector('[data-header]');
      if (header && header._virsaayDesktopMenu && header._virsaayDesktopMenu.isOpen()) {
        header._virsaayDesktopMenu.close(false);
      }
      if (window._virsaayMobileNav && window._virsaayMobileNav.isOpen()) {
        window._virsaayMobileNav.close(null, false);
      }

      var sc = scrim();
      root.hidden = false;
      // The class is what actually puts the panel on screen.
      root.classList.add('is-open');
      root.setAttribute('aria-hidden', 'false');
      if (sc) sc.hidden = false;
      setExpanded(true);

      var m = motion();
      if (m && typeof m.lockScroll === 'function') m.lockScroll(true);

      if (reduced()) {
        if (sc) sc.style.opacity = '1';
      } else {
        window.gsap.fromTo(
          root,
          { yPercent: -100, opacity: 0 },
          { yPercent: 0, opacity: 1, duration: m.dur(0.4), ease: 'power3.out' }
        );
        if (sc) window.gsap.to(sc, { opacity: 1, duration: m.dur(0.4) });
      }

      // Focus after the panel is on screen, or the browser scrolls to find it.
      requestAnimationFrame(function () {
        if (input) {
          input.focus();
          // Put the caret after any existing term rather than selecting it.
          var end = input.value.length;
          try { input.setSelectionRange(end, end); } catch (err) {}
        }
        if (m && typeof m.trapFocus === 'function') m.trapFocus(root, input);
      });
    }

    function finishClose() {
      if (isOpen) return;
      root.hidden = true;
      root.classList.remove('is-open');
      root.setAttribute('aria-hidden', 'true');
      if (window.gsap) window.gsap.set(root, { clearProps: 'transform,opacity' });
      var sc = scrim();
      if (sc) {
        sc.hidden = true;
        sc.style.opacity = '';
      }
    }

    function close(e) {
      if (e && e.preventDefault) e.preventDefault();
      if (!isOpen) return;
      isOpen = false;

      clearTimeout(timer);
      if (controller) {
        controller.abort();
        controller = null;
      }

      setExpanded(false);
      var m = motion();
      if (m && typeof m.releaseFocus === 'function') m.releaseFocus(false, root);
      if (m && typeof m.lockScroll === 'function') m.lockScroll(false);

      if (reduced()) {
        finishClose();
      } else {
        var sc = scrim();
        window.gsap.to(root, {
          yPercent: -100,
          opacity: 0,
          duration: m.dur(0.3),
          ease: 'power3.in',
          onComplete: finishClose
        });
        if (sc) window.gsap.to(sc, { opacity: 0, duration: m.dur(0.3) });
      }

      if (activeElement && activeElement.isConnected) activeElement.focus();
      activeElement = null;
    }

    /* -- Predictive results -------------------------------------------- */

    function clearResults() {
      results.innerHTML = '';
      if (suggestions) suggestions.hidden = false;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
    }

    function predictiveUrl(query) {
      var routes = (window.VirsaayTheme && window.VirsaayTheme.routes) || {};
      var base = routes.predictiveSearch || '/search/suggest';
      return (
        base +
        '?q=' + encodeURIComponent(query) +
        '&resources[type]=' + encodeURIComponent(RESOURCES) +
        '&resources[limit]=' + RESOURCE_LIMIT +
        '&resources[limit_scope]=' + RESOURCE_SCOPE +
        '&section_id=predictive-search'
      );
    }

    function fetchResults(query) {
      if (!query) {
        clearResults();
        return;
      }

      if (suggestions) suggestions.hidden = true;

      if (controller) controller.abort();
      controller = new AbortController();

      fetch(predictiveUrl(query), { signal: controller.signal })
        .then(function (res) {
          if (!res.ok) throw new Error('Search request failed');
          return res.text();
        })
        .then(function (text) {
          var doc = new DOMParser().parseFromString(text, 'text/html');
          var fresh = doc.querySelector('#predictive-search-results');

          if (!fresh) {
            clearResults();
            return;
          }

          results.innerHTML = fresh.innerHTML;
          input.setAttribute('aria-expanded', 'true');
          input.removeAttribute('aria-activedescendant');

          if (!reduced()) {
            var items = results.querySelectorAll('.virsaay-predictive__item, .virsaay-predictive__foot');
            if (items.length) {
              window.gsap.fromTo(
                items,
                { opacity: 0, y: 10 },
                { opacity: 1, y: 0, stagger: 0.05, duration: 0.3, ease: 'power2.out', clearProps: 'transform,opacity' }
              );
            }
          }
        })
        .catch(function (err) {
          if (err.name === 'AbortError') return;
          // Leave whatever is on screen rather than blanking the panel; the
          // form still submits to the full results page.
          clearResults();
        });
    }

    function onInput() {
      clearTimeout(timer);
      var query = input.value.trim();
      timer = setTimeout(function () {
        fetchResults(query);
      }, 250);
    }

    function submitSearch() {
      if (!form || !input || input.value.trim() === '') return;
      // requestSubmit fires the submit event (so the guard below still runs);
      // submit() is the fallback where it is missing or throws.
      try {
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit();
          return;
        }
      } catch (err) {
        // fall through
      }
      form.submit();
    }

    function options() {
      return Array.prototype.slice.call(results.querySelectorAll('[role="option"]'));
    }

    function onKeyDown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
        return;
      }

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        var opts = options();
        if (!opts.length) return;

        e.preventDefault();
        var active = results.querySelector('[aria-selected="true"]');
        var index = opts.indexOf(active);
        if (active) active.setAttribute('aria-selected', 'false');

        if (e.key === 'ArrowDown') index = index < opts.length - 1 ? index + 1 : 0;
        else index = index > 0 ? index - 1 : opts.length - 1;

        var next = opts[index];
        next.setAttribute('aria-selected', 'true');
        if (next.id) input.setAttribute('aria-activedescendant', next.id);
        if (next.scrollIntoView) next.scrollIntoView({ block: 'nearest' });
        return;
      }

      if (e.key === 'Enter') {
        var link = results.querySelector('[aria-selected="true"] a');
        if (link) {
          e.preventDefault();
          link.click();
        }
        // Otherwise the form submits to the full results page on its own.
      }
    }

    if (input) {
      input.addEventListener('input', onInput);
      input.addEventListener('keydown', onKeyDown);
    }

    // Delegated, so controls that arrive with re-rendered results still work.
    root.addEventListener('click', function (e) {
      if (e.target.closest('[data-search-close]')) {
        close(e);
        return;
      }
      // "Search for all results" — previously an inert <button>.
      if (e.target.closest('[data-predictive-search-submit]')) {
        e.preventDefault();
        submitSearch();
      }
    });

    // Never submit an empty query to the results page.
    if (form) {
      form.addEventListener('submit', function (e) {
        if (!input || input.value.trim() === '') e.preventDefault();
      });
    }

    document.addEventListener('click', function (e) {
      var opener = e.target.closest('[data-search-open]');
      if (opener) {
        open(e);
        return;
      }
      var sc = scrim();
      if (isOpen && sc && e.target === sc) close(e);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen) close(e);
    });
  }

  function boot() {
    document.querySelectorAll(SELECTOR).forEach(initSearch);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (!event.target.querySelector) return;
    var root = event.target.querySelector(SELECTOR);
    if (root) initSearch(root);
  });
})();
